/**
 * Setup & goal — the SPECIALIST variant (spec #47 §3a).
 *
 * Two kinds of agent, two backends, one component family. A specialist
 * holds no beliefs and no leash: its setup is `cadence_override` plus the
 * `SpecialistConfig` fields the onboarding interview already writes. Those
 * fields are editable NOWHERE today — the `PATCH` endpoint shipped with no
 * caller, so changing an ICP after onboarding meant re-running the whole
 * interview. This card adopts that endpoint unchanged (P10); routing
 * specialist config through the agent settings service instead would be
 * the parallel store §12 forbids.
 *
 * PROVENANCE HAS NO BACKING COLUMN ANYWHERE for these fields, so it is
 * computed the same way as the leash: re-derive from the interview answers
 * and compare (P4). Equal reads "from your setup interview"; different
 * reads you/operator; absent reads default; and with no session left to
 * compare against, everything set reads `source unrecorded` rather than
 * claiming a lineage nothing records.
 *
 * The plan ceiling and the system ceiling are two (HANDOFF §12). Hourly
 * cadence above the tenant's plan renders LOCKED with its effective value,
 * the required plan and one upgrade link — never a disabled empty shell,
 * never silently missing. For an operator the same field unlocks to the
 * system ceiling and the chip says "operator", because the ceiling being
 * lifted is the tenant's plan, not the platform's floor.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Select,
  Text,
  TextField,
} from '@shopify/polaris';

import { getSession } from '../../../services/onboardingApi';
import {
  listSpecialists,
  updateSpecialistSettings,
} from '../../../services/marketingSuiteApi';
import type { Actor, FieldPolicy } from '../../../types/agentSettings';
import type { Specialist } from '../../../types/marketingSuite';
import { ProvenanceTag } from './ProvenanceTag';
import {
  classifyCeiling,
  isWritable,
  specialistProvenance,
  type Reader,
} from './provenance';

/** The §3a inventory: exactly the fields `derive_configs` writes. */
const CONFIG_FIELDS: Array<{
  key: string;
  label: string;
  kind: 'text' | 'number' | 'list';
}> = [
  { key: 'monthly_budget', label: 'Monthly budget', kind: 'number' },
  { key: 'icp', label: 'Ideal customer', kind: 'text' },
  { key: 'brand_voice', label: 'Brand voice', kind: 'text' },
  { key: 'value_prop', label: 'Value proposition', kind: 'text' },
  { key: 'seed_topics', label: 'Seed topics', kind: 'list' },
  { key: 'competitors', label: 'Competitors', kind: 'list' },
];

function toInput(value: unknown, kind: string): string {
  if (value == null) return '';
  if (kind === 'list') return Array.isArray(value) ? value.join(', ') : '';
  return String(value);
}

function fromInput(raw: string, kind: string): unknown {
  const trimmed = raw.trim();
  if (kind === 'list') {
    return trimmed
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (kind === 'number') return trimmed === '' ? null : Number(trimmed);
  return trimmed;
}

export function SpecialistSetupCard({
  specialistKey,
}: {
  specialistKey: string;
}) {
  const [specialist, setSpecialist] = useState<Specialist | null>(null);
  const [actor, setActor] = useState<Actor | null>(null);
  const [policy, setPolicy] = useState<FieldPolicy>({});
  const [answers, setAnswers] = useState<Record<string, unknown> | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [refusals, setRefusals] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    const listing = await listSpecialists();
    const found =
      listing.specialists.find((s) => s.key === specialistKey) ?? null;
    setSpecialist(found);
    setActor(listing.actor ?? null);
    setPolicy(listing.field_policy ?? {});

    // No session is not an error: it is the reason the provenance rule
    // degrades to "source unrecorded" for everything set.
    const lookup = await getSession().catch(() => null);
    setAnswers(lookup?.session?.answers ?? null);
  }, [specialistKey]);

  useEffect(() => {
    void load().catch(() => {
      setSpecialist(null);
    });
  }, [load]);

  const reader: Reader = {
    role: actor?.role ?? 'tenant',
    user_id: actor?.user_id ?? null,
  };
  const isOperator = reader.role === 'operator';

  const save = useCallback(
    async (field: string, update: Parameters<typeof updateSpecialistSettings>[1]) => {
      setSaving(field);
      setRefusals((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      try {
        const result = await updateSpecialistSettings(specialistKey, update);
        setSpecialist((prev) =>
          prev
            ? {
                ...prev,
                config: result?.config ?? prev.config,
                effective_cadence:
                  result?.cadence_override ?? prev.effective_cadence,
              }
            : prev
        );
      } catch (e) {
        // Rendered at the field, with the value that was typed still
        // there — the server refuses, and the human corrects.
        const detail =
          (e as Error & { detail?: string }).detail ||
          (e instanceof Error ? e.message : 'Could not save that.');
        setRefusals((prev) => ({ ...prev, [field]: detail }));
      } finally {
        setSaving(null);
      }
    },
    [specialistKey]
  );

  if (!specialist) {
    return (
      <Card>
        <Text as="p" tone="subdued">
          Loading this agent&apos;s setup…
        </Text>
      </Card>
    );
  }

  const config = specialist.config ?? {};
  const cadenceCeiling = classifyCeiling(policy.cadence_override);
  const cadenceOptions =
    ((policy.cadence_override?.floor as { allowed?: string[] } | undefined)
      ?.allowed ?? ['daily', 'weekly', 'monthly']).map((value) => ({
      label: value,
      value,
    }));

  return (
    <Card>
      <BlockStack gap="400">
        {isOperator && (
          <Banner tone="warning">
            <Text as="p">
              You are acting as an operator in this workspace, as yourself.
              Every change is recorded against your user.
            </Text>
          </Banner>
        )}

        <Text as="h2" variant="headingMd">
          Setup &amp; goal
        </Text>

        {/* --- cadence: the one real plan gate the platform has --------- */}
        <BlockStack gap="150">
          <InlineStack gap="200" blockAlign="center" wrap>
            <Text as="h3" variant="headingXs">
              Cadence
            </Text>
            {cadenceCeiling === 'plan_locked' && (
              <Badge tone="attention">
                {`needs the ${policy.cadence_override?.required_plan ?? 'higher'} plan`}
              </Badge>
            )}
            {cadenceCeiling === 'operator_only' && (
              <Badge tone="attention">operator</Badge>
            )}
          </InlineStack>
          {/* Locked, never hidden: the value it ACTUALLY runs at is on
              screen either way. */}
          <Text as="p">{`Runs ${specialist.effective_cadence}`}</Text>
          {isWritable(cadenceCeiling) ? (
            <>
              <Select
                label="Cadence"
                options={cadenceOptions}
                value={drafts.cadence_override ?? specialist.effective_cadence}
                onChange={(value) =>
                  setDrafts((prev) => ({ ...prev, cadence_override: value }))
                }
              />
              <InlineStack gap="200">
                <Button
                  accessibilityLabel="Save Cadence"
                  loading={saving === 'cadence_override'}
                  onClick={() =>
                    void save('cadence_override', {
                      cadence_override:
                        drafts.cadence_override ?? specialist.effective_cadence,
                    })
                  }
                >
                  Save
                </Button>
              </InlineStack>
            </>
          ) : (
            <BlockStack gap="100">
              <Text as="p" variant="bodySm" tone="subdued">
                {policy.cadence_override?.reason}
              </Text>
              <Text as="p" variant="bodySm">
                <Link to="/billing">See plans</Link>
              </Text>
            </BlockStack>
          )}
          {refusals.cadence_override ? (
            <Banner tone="critical">{refusals.cadence_override}</Banner>
          ) : null}
        </BlockStack>

        {/* --- the interview-written config ---------------------------- */}
        {CONFIG_FIELDS.map(({ key, label, kind }) => {
          const provenance = specialistProvenance({
            key: specialistKey,
            field: key,
            value: config[key],
            answers,
            reader,
          });
          const ceiling = classifyCeiling(policy[key]);
          const value = drafts[key] ?? toInput(config[key], kind);
          return (
            <BlockStack key={key} gap="150">
              <InlineStack gap="200" blockAlign="center" wrap>
                <ProvenanceTag provenance={provenance} />
              </InlineStack>
              <TextField
                label={label}
                autoComplete="off"
                type={kind === 'number' ? 'number' : 'text'}
                value={value}
                disabled={!isWritable(ceiling)}
                helpText={
                  kind === 'list'
                    ? `${policy[key]?.reason ?? ''} Separate with commas.`
                    : policy[key]?.reason ?? undefined
                }
                onChange={(next) =>
                  setDrafts((prev) => ({ ...prev, [key]: next }))
                }
              />
              {refusals[key] ? (
                <Banner tone="critical">{refusals[key]}</Banner>
              ) : null}
              {isWritable(ceiling) && (
                <InlineStack gap="200">
                  <Button
                    accessibilityLabel={`Save ${label}`}
                    loading={saving === key}
                    onClick={() =>
                      void save(key, {
                        config: { ...config, [key]: fromInput(value, kind) },
                      })
                    }
                  >
                    Save
                  </Button>
                </InlineStack>
              )}
            </BlockStack>
          );
        })}
      </BlockStack>
    </Card>
  );
}

export default SpecialistSetupCard;
